import { useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ESTIMATE_STATUS_LABELS,
  formatAddress,
  formatCents,
  formatDate,
  formatWindow,
  mapsUrl,
  type Property,
} from '@fsm/shared';
import { api } from '../lib/api';
import { DeleteRecord } from '../components/Selection';
import { keys, useJobs } from '../lib/queries';
import { useAuth } from '../lib/auth';
import { NewJobModal } from '../components/NewJobModal';
import { PhoneLine } from '../components/CallActions';
import { CommunicationLog } from '../components/CommunicationLog';
import { RecordHeader } from '../components/record';
import { ContactCard } from '../components/ContactCard';
import { Icon } from '../components/icons';
import { PageFooter, useCrumb } from '../components/Layout';
import {
  Avatar,
  Badge,
  Button,
  Card,
  cx,
  Dot,
  Field,
  Input,
  Modal,
  Select,
  Spinner,
  StatusBadge,
  Table,
  Td,
  Textarea,
  Th,
} from '../components/ui';

/**
 * The customer page.
 *
 * The same three columns as a job, an estimate and an invoice — who on the
 * left, their work in the middle, what was said to them on the right. The one
 * rule this page adds is that an action lives beside the thing it acts on: the
 * plus that books a job is on the jobs card, the plus that adds an address is
 * on the addresses card, and the phone number dials itself. A header full of
 * buttons makes you read all of them to find the one you want.
 */
export function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can } = useAuth();
  const [addingProperty, setAddingProperty] = useState(false);
  const [editingProperty, setEditingProperty] = useState<Property | null>(null);
  const [creatingJob, setCreatingJob] = useState(false);
  const [editing, setEditing] = useState(false);
  const [creatingEstimate, setCreatingEstimate] = useState(false);
  const [billing, setBilling] = useState(false);

  const isOffice = can('admin', 'dispatcher');

  const { data: customer, isLoading } = useQuery({
    queryKey: keys.customer(id ?? ''),
    queryFn: () => api.customers.get(id!),
    enabled: Boolean(id),
  });

  useCrumb(customer?.name ?? null);

  const { data: jobs = [] } = useJobs({ customerId: id, limit: 100 }, Boolean(id));

  const { data: invoices = [] } = useQuery({
    queryKey: keys.invoices(`customer:${id}`),
    queryFn: () => api.invoices.list({ customerId: id }),
    select: (d) => d.items,
    enabled: Boolean(id) && isOffice,
  });

  const { data: estimates = [] } = useQuery({
    queryKey: keys.estimates(`customer:${id}`),
    queryFn: () => api.estimates.list({ customerId: id }),
    select: (d) => d.items,
    enabled: Boolean(id) && isOffice,
  });

  if (isLoading || !customer) {
    return (
      <div className="grid flex-1 place-items-center">
        <Spinner className="size-6 text-ink-4" />
      </div>
    );
  }

  const outstanding = customer.outstandingCents ?? 0;
  const properties = customer.properties ?? [];
  const primaryProperty = properties.find((p) => p.isPrimary) ?? properties[0];

  return (
    <>
      <RecordHeader
        eyebrow="Customer profile"
        avatar={
          <Avatar
            name={customer.name}
            color="#eae7de"
            textColor="#8a7958"
            size={59}
            radius="15px"
          />
        }
        title={customer.name}
        badges={outstanding > 0 && <Badge tone="red">{formatCents(outstanding)} outstanding</Badge>}
        subtitle={
          <>
            Residential customer
            {primaryProperty && (
              <>
                <Dot />
                {[primaryProperty.city, primaryProperty.state].filter(Boolean).join(', ')}
              </>
            )}
          </>
        }
        actions={
          isOffice && (
            <>
              {customer.phone && (
                <PhoneLine
                  phone={customer.phone}
                  customerId={customer.id}
                  customerName={customer.name}
                  textOnly
                />
              )}
              <Button variant="primary" icon="users" onClick={() => setEditing(true)}>
                Edit customer
              </Button>
            </>
          )
        }
      />

      <div className="min-h-0 flex-1 overflow-y-auto px-[30px]">
        <div className="mx-auto grid max-w-[1860px] grid-cols-1 items-start gap-5 lg:grid-cols-[320px_minmax(0,1fr)] xl:grid-cols-[minmax(265px,0.9fr)_minmax(560px,2.6fr)_minmax(280px,1fr)]">
          {/* ---------------------------------------------------- left rail */}
          <div className="space-y-5">
            <ContactCard
              name={customer.name}
              role="Residential customer"
              phone={customer.phone}
              email={customer.email}
              customerId={customer.id}
            >
              {isOffice && (
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  className="text-[13px] font-semibold text-accent hover:underline"
                >
                  Edit details
                </button>
              )}
            </ContactCard>

            <Card
              title="Addresses"
              icon="pin"
              count={properties.length}
              divided
              bodyClassName="p-0"
              action={
                isOffice && (
                  <AddButton label="Add an address" onClick={() => setAddingProperty(true)} />
                )
              }
            >
              {properties.length === 0 ? (
                <p className="px-[21px] py-5 text-[13px] text-ink-4">No addresses on file.</p>
              ) : (
                <ul>
                  {properties.map((property) => (
                    <li
                      key={property.id}
                      className="border-b border-line px-[21px] py-[18px] last:border-b-0"
                    >
                      <div className="mb-3 flex items-center justify-between gap-2">
                        <div className="flex min-w-0 items-center gap-2">
                          {property.isPrimary && <Badge tone="green">Primary</Badge>}
                          {property.label && (
                            <span className="truncate text-[12px] text-ink-4">{property.label}</span>
                          )}
                        </div>
                        {isOffice && (
                          <button
                            type="button"
                            onClick={() => setEditingProperty(property)}
                            className="shrink-0 text-[13px] font-semibold text-accent hover:underline"
                          >
                            Edit
                          </button>
                        )}
                      </div>
                      <a
                        href={mapsUrl(property, 'other')}
                        target="_blank"
                        rel="noreferrer"
                        className="block text-[14px] leading-[1.9] text-ink-2 hover:text-accent"
                      >
                        {formatAddress(property)}
                      </a>
                      {property.gateCode && (
                        <div className="mt-1 text-[12px] text-ink-4">
                          Gate code {property.gateCode}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card
              title="Notes"
              divided
              bodyClassName="p-0"
              action={
                isOffice && (
                  <button
                    type="button"
                    onClick={() => setEditing(true)}
                    className="text-[13px] font-semibold text-accent hover:underline"
                  >
                    Edit
                  </button>
                )
              }
            >
              {/*
               * A note is somebody's voice, not a field. The warm ground and
               * the quill mark it as written by a person rather than recorded
               * by the system — which is exactly how it should be read.
               */}
              <div className="flex gap-2.5 bg-[#fffcf5] px-[21px] py-5">
                <Icon name="chat" size={17} className="mt-0.5 text-[#afa17b]" />
                <p className="min-w-0 text-[14px] leading-[1.8] whitespace-pre-wrap text-[#7d735a]">
                  {customer.notes || `Nothing noted.${isOffice ? ' Edit the customer to add some.' : ''}`}
                </p>
              </div>
            </Card>
          </div>

          {/* ------------------------------------------------------- middle */}
          <div className="space-y-5">
            <Card
              title="Jobs"
              icon="job"
              count={jobs.length}
              divided
              bodyClassName="p-0"
              action={
                isOffice && <AddButton label="Book a job" onClick={() => setCreatingJob(true)} />
              }
            >
              {jobs.length === 0 ? (
                <Empty>No jobs yet.</Empty>
              ) : (
                <Table>
                  <thead>
                    <tr>
                      <Th className="w-[46%]">Job</Th>
                      <Th>Scheduled</Th>
                      <Th>Value</Th>
                      <Th align="right">Status</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map((job) => (
                      <tr
                        key={job.id}
                        onClick={() => navigate(`/jobs/${job.id}`)}
                        className="cursor-pointer hover:bg-raised"
                      >
                        <Td>
                          {/*
                           * Title first, then the number and where it is. The
                           * number used to have a column of its own, which
                           * spent a tenth of the table on four digits nobody
                           * scans a list by.
                           */}
                          <div className="text-[14px] font-semibold text-ink">{job.title}</div>
                          <div className="mt-1 text-[12px] text-ink-4">
                            #{job.number}
                            {job.property && (
                              <>
                                <Dot />
                                {formatAddress(job.property)}
                              </>
                            )}
                          </div>
                        </Td>
                        <Td className="tnum">
                          {job.scheduledStart && job.scheduledEnd ? (
                            <>
                              <div>{formatDate(job.scheduledStart)}</div>
                              <div className="mt-1 text-[12px] text-ink-4">
                                {formatWindow(job.scheduledStart, job.scheduledEnd)}
                              </div>
                            </>
                          ) : (
                            <span className="text-ink-4">Not scheduled</span>
                          )}
                        </Td>
                        <Td className="tnum">{formatCents(job.totals?.totalCents ?? 0)}</Td>
                        <Td align="right">
                          <StatusBadge status={job.status} />
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card>

            {isOffice && (
              <Card
                title="Estimates"
                icon="file"
                count={estimates.length}
                divided
                bodyClassName="p-0"
                action={
                  <AddButton label="New estimate" onClick={() => setCreatingEstimate(true)} />
                }
              >
                {estimates.length === 0 ? (
                  <Empty>Nothing quoted.</Empty>
                ) : (
                  <Table>
                    <thead>
                      <tr>
                        <Th className="w-[46%]">Estimate</Th>
                        <Th>Valid until</Th>
                        <Th align="right">Status</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {estimates.map((estimate) => (
                        <tr
                          key={estimate.id}
                          onClick={() => navigate(`/estimates/${estimate.id}`)}
                          className="cursor-pointer hover:bg-raised"
                        >
                          <Td>
                            <div className="text-[14px] font-semibold text-ink">
                              {estimate.title}
                            </div>
                            <div className="mt-1 text-[12px] text-ink-4">#{estimate.number}</div>
                          </Td>
                          <Td className="tnum">
                            {estimate.validUntil ? formatDate(estimate.validUntil) : 'Not set'}
                          </Td>
                          <Td align="right">
                            <Badge
                              tone={
                                estimate.status === 'approved'
                                  ? 'emerald'
                                  : estimate.status === 'declined'
                                    ? 'red'
                                    : 'slate'
                              }
                            >
                              {ESTIMATE_STATUS_LABELS[estimate.status]}
                            </Badge>
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card>
            )}

            {isOffice && (
              <Card
                title="Invoices"
                icon="cost"
                count={invoices.length}
                divided
                bodyClassName="p-0"
              >
                {invoices.length === 0 ? (
                  <Empty>Nothing billed.</Empty>
                ) : (
                  <Table>
                    <thead>
                      <tr>
                        <Th className="w-[46%]">Invoice</Th>
                        <Th>Due</Th>
                        <Th>Total</Th>
                        <Th align="right">Balance</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((invoice) => (
                        <tr
                          key={invoice.id}
                          onClick={() => navigate(`/invoices/${invoice.id}`)}
                          className="cursor-pointer hover:bg-raised"
                        >
                          <Td>
                            <div className="text-[14px] font-semibold text-ink">
                              Invoice #{invoice.number}
                            </div>
                            <div className="mt-1 text-[12px] text-ink-4">
                              {invoice.issuedAt ? `Issued ${formatDate(invoice.issuedAt)}` : 'Draft'}
                            </div>
                          </Td>
                          <Td className="tnum">
                            {invoice.dueAt ? formatDate(invoice.dueAt) : '—'}
                          </Td>
                          <Td className="tnum">{formatCents(invoice.totals?.totalCents ?? 0)}</Td>
                          <Td align="right">
                            {invoice.totals?.isSettled ? (
                              <Badge tone="emerald">Paid</Badge>
                            ) : (
                              <span className="font-semibold tnum text-danger">
                                {formatCents(invoice.totals?.balanceDueCents ?? 0)}
                              </span>
                            )}
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card>
            )}
          </div>

          {/* --------------------------------------------------- right rail */}
          <div className="space-y-5">
            {isOffice && <CommunicationLog customerId={customer.id} limit={40} />}
          </div>
        </div>

        {can('admin') && (
          <DeleteRecord
            one="customer"
            many="customers"
            warning="Their addresses, notes and recurring schedules go too. A customer with jobs or invoices cannot be deleted — you will be told what is in the way."
            onConfirm={() => api.customers.bulkRemove([customer.id])}
            onDeleted={() => {
              void qc.invalidateQueries({ queryKey: ['customers'] });
              navigate('/customers');
            }}
          />
        )}

        <PageFooter label={customer.name} />
      </div>

      <EditPropertyModal
        property={editingProperty}
        onClose={() => setEditingProperty(null)}
        onSaved={() => {
          setEditingProperty(null);
          void qc.invalidateQueries({ queryKey: keys.customer(customer.id) });
        }}
      />

      <AddPropertyModal
        open={addingProperty}
        onClose={() => setAddingProperty(false)}
        customerId={customer.id}
        onSaved={() => {
          setAddingProperty(false);
          void qc.invalidateQueries({ queryKey: keys.customer(customer.id) });
        }}
      />

      <NewJobModal
        open={creatingJob}
        onClose={() => setCreatingJob(false)}
        defaultCustomerId={customer.id}
        onCreated={(job) => navigate(`/jobs/${job.id}`)}
      />

      <EditCustomerModal
        open={editing}
        onClose={() => setEditing(false)}
        customer={customer}
        onSaved={() => {
          setEditing(false);
          void qc.invalidateQueries({ queryKey: keys.customer(customer.id) });
          void qc.invalidateQueries({ queryKey: ['customers'] });
        }}
      />

      <NewEstimateModal
        open={creatingEstimate}
        onClose={() => setCreatingEstimate(false)}
        customerId={customer.id}
        properties={properties}
        onCreated={(estimate) => navigate(`/estimates/${estimate.id}`)}
      />

      <BillJobModal
        open={billing}
        onClose={() => setBilling(false)}
        jobs={jobs}
        onCreated={(invoice) => navigate(`/invoices/${invoice.id}`)}
      />
    </>
  );
}

/** The plus that sits in a card header, beside the list it adds to. */
function AddButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className={cx(
        'grid size-8 place-items-center rounded-[7px] border border-line text-ink-3 transition-colors',
        'hover:border-accent-line hover:bg-accent-soft hover:text-accent',
      )}
    >
      <Icon name="plus" size={16} />
    </button>
  );
}

function Empty({ children }: { children: ReactNode }) {
  return <p className="px-4 py-5 text-center text-[12.5px] text-ink-4">{children}</p>;
}

function AddPropertyModal({
  open,
  onClose,
  customerId,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  customerId: string;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    label: '',
    line1: '',
    city: '',
    state: 'TX',
    postalCode: '',
    gateCode: '',
  });
  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const save = useMutation({
    mutationFn: () =>
      api.customers.addProperty(customerId, {
        label: form.label.trim() || null,
        line1: form.line1.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        postalCode: form.postalCode.trim(),
        gateCode: form.gateCode.trim() || null,
      }),
    onSuccess: onSaved,
  });

  const canSubmit =
    form.line1.trim() && form.city.trim() && form.state.trim() && form.postalCode.trim();

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add an address"
      description="A customer can have as many as they need — a house, a rental, a shop."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!canSubmit}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            Add
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Label" hint="optional — “Rental”, “Shop”">
          <Input value={form.label} onChange={set('label')} />
        </Field>
        <Field label="Street address" required>
          <Input value={form.line1} onChange={set('line1')} />
        </Field>
        <div className="grid grid-cols-[2fr_1fr_1fr] gap-3">
          <Field label="City" required>
            <Input value={form.city} onChange={set('city')} />
          </Field>
          <Field label="State" required>
            <Input value={form.state} onChange={set('state')} />
          </Field>
          <Field label="ZIP" required>
            <Input value={form.postalCode} onChange={set('postalCode')} />
          </Field>
        </div>
        <Field label="Gate code" hint="optional">
          <Input value={form.gateCode} onChange={set('gateCode')} />
        </Field>
      </div>
    </Modal>
  );
}

/**
 * Everything about the customer themselves, in one dialog.
 *
 * Four fields that are almost always corrected together — somebody rings in
 * with a new number and a married name — so they are saved together rather
 * than through four separate inline editors.
 */
function EditCustomerModal({
  open,
  onClose,
  customer,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  customer: { id: string; name: string; email?: string | null; phone?: string | null; notes?: string | null };
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    name: customer.name,
    phone: customer.phone ?? '',
    email: customer.email ?? '',
    notes: customer.notes ?? '',
  });
  const [error, setError] = useState<string | null>(null);

  // Reopening on a customer whose details changed elsewhere should show the
  // current values, not the ones captured the first time this mounted.
  const [seen, setSeen] = useState(customer.id + ':' + open);
  const key = customer.id + ':' + open;
  if (key !== seen) {
    setSeen(key);
    if (open) {
      setForm({
        name: customer.name,
        phone: customer.phone ?? '',
        email: customer.email ?? '',
        notes: customer.notes ?? '',
      });
      setError(null);
    }
  }

  const save = useMutation({
    mutationFn: () =>
      api.customers.update(customer.id, {
        name: form.name.trim(),
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        notes: form.notes.trim() || null,
      }),
    onSuccess: onSaved,
    onError: (e) => setError((e as { message?: string }).message ?? 'Could not save'),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit customer"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!form.name.trim()}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Name" required>
          <Input
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            autoFocus
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Phone">
            <Input
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            />
          </Field>
          <Field label="Email">
            <Input
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            />
          </Field>
        </div>
        <Field label="Notes" hint="what is true of this customer every visit">
          <Textarea
            rows={4}
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            placeholder="Dog in the back yard. Billing goes to the property manager."
          />
        </Field>

        {error && (
          <p className="rounded-lg bg-danger-soft px-3 py-2 text-[13px] text-danger">{error}</p>
        )}
      </div>
    </Modal>
  );
}

/**
 * A new estimate: a title and which address it is for.
 *
 * The line items are added on the estimate itself, against the price book —
 * asking for them here would be a second, worse copy of that editor.
 */
function NewEstimateModal({
  open,
  onClose,
  customerId,
  properties,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  customerId: string;
  properties: Array<{ id: string; label?: string | null; isPrimary: boolean; line1: string; city: string; state: string; postalCode: string }>;
  onCreated: (estimate: { id: string }) => void;
}) {
  const [title, setTitle] = useState('');
  const [propertyId, setPropertyId] = useState(
    () => properties.find((p) => p.isPrimary)?.id ?? properties[0]?.id ?? '',
  );
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api.estimates.create({ customerId, propertyId, title: title.trim(), lineItems: [] }),
    onSuccess: onCreated,
    onError: (e) => setError((e as { message?: string }).message ?? 'Could not create the estimate'),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New estimate"
      description="Add the line items on the next screen."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!title.trim() || !propertyId}
            loading={create.isPending}
            onClick={() => create.mutate()}
          >
            Create
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="What is it for" required>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Full system replacement — 3 ton heat pump"
            autoFocus
          />
        </Field>
        <Field label="Address" required>
          {properties.length === 0 ? (
            <p className="text-[13px] text-ink-3">
              This customer has no address on file yet — add one first.
            </p>
          ) : (
            <Select value={propertyId} onChange={(e) => setPropertyId(e.target.value)}>
              {properties.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label ? `${p.label} — ` : ''}
                  {formatAddress(p)}
                </option>
              ))}
            </Select>
          )}
        </Field>

        {error && (
          <p className="rounded-lg bg-danger-soft px-3 py-2 text-[13px] text-danger">{error}</p>
        )}
      </div>
    </Modal>
  );
}

/**
 * Billing a job.
 *
 * An invoice is always raised from a job — that is the server's rule, and it is
 * what gives every invoice a line back to the work that earned it. So the plus
 * on the invoices card asks which job rather than opening a blank invoice, and
 * when there is nothing billable it says why instead of failing on submit.
 */
function BillJobModal({
  open,
  onClose,
  jobs,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  jobs: Array<{ id: string; number: number; title: string; status: string; invoiceId?: string | null; completedAt: string | null; totals?: { totalCents: number } }>;
  onCreated: (invoice: { id: string }) => void;
}) {
  const billable = jobs.filter((j) => j.status === 'completed' && !j.invoiceId);
  const [jobId, setJobId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const chosen = jobId || billable[0]?.id || '';

  const create = useMutation({
    mutationFn: () => api.invoices.createFromJob(chosen, { dueInDays: 14 }),
    onSuccess: onCreated,
    onError: (e) => setError((e as { message?: string }).message ?? 'Could not create the invoice'),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Bill a job"
      description="An invoice is raised from the job it bills, so it carries the same line items."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!chosen}
            loading={create.isPending}
            onClick={() => create.mutate()}
          >
            Create invoice
          </Button>
        </>
      }
    >
      {billable.length === 0 ? (
        <p className="text-[13px] text-ink-3">
          Nothing to bill. A job has to be finished and not already invoiced before it can be
          billed — mark one complete on its own page and it will appear here.
        </p>
      ) : (
        <div className="space-y-3">
          <Field label="Job" required>
            <Select value={chosen} onChange={(e) => setJobId(e.target.value)}>
              {billable.map((job) => (
                <option key={job.id} value={job.id}>
                  #{job.number} — {job.title}
                  {job.totals ? ` · ${formatCents(job.totals.totalCents)}` : ''}
                </option>
              ))}
            </Select>
          </Field>

          {error && (
            <p className="rounded-lg bg-danger-soft px-3 py-2 text-[13px] text-danger">{error}</p>
          )}
        </div>
      )}
    </Modal>
  );
}

/**
 * Correcting an address.
 *
 * Same fields as adding one, and deliberately the same dialog shape — the
 * difference between "add" and "fix a typo in" is which record it writes to,
 * and making them look different would suggest otherwise.
 *
 * Takes the property itself rather than an `open` flag so that opening it on a
 * second address cannot show the first one's values: the form is keyed to the
 * record, so a different record is a different form.
 */
function EditPropertyModal({
  property,
  onClose,
  onSaved,
}: {
  property: Property | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  if (!property) return null;
  return (
    <PropertyForm key={property.id} property={property} onClose={onClose} onSaved={onSaved} />
  );
}

function PropertyForm({
  property,
  onClose,
  onSaved,
}: {
  property: Property;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    label: property.label ?? '',
    line1: property.line1,
    city: property.city,
    state: property.state,
    postalCode: property.postalCode,
    gateCode: property.gateCode ?? '',
    isPrimary: property.isPrimary,
  });
  const [error, setError] = useState<string | null>(null);

  const set = (key: 'label' | 'line1' | 'city' | 'state' | 'postalCode' | 'gateCode') =>
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((f) => ({ ...f, [key]: e.target.value }));

  const save = useMutation({
    mutationFn: () =>
      api.customers.updateProperty(property.id, {
        label: form.label.trim() || null,
        line1: form.line1.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        postalCode: form.postalCode.trim(),
        gateCode: form.gateCode.trim() || null,
        isPrimary: form.isPrimary,
      }),
    onSuccess: onSaved,
    onError: (e) => setError((e as { message?: string }).message ?? 'Could not save the address'),
  });

  const canSubmit =
    form.line1.trim() && form.city.trim() && form.state.trim() && form.postalCode.trim();

  return (
    <Modal
      open
      onClose={onClose}
      title="Edit address"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!canSubmit}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Label" hint="optional — “Rental”, “Shop”">
          <Input value={form.label} onChange={set('label')} />
        </Field>
        <Field label="Street address" required>
          <Input value={form.line1} onChange={set('line1')} autoFocus />
        </Field>
        <div className="grid grid-cols-[2fr_1fr_1fr] gap-3">
          <Field label="City" required>
            <Input value={form.city} onChange={set('city')} />
          </Field>
          <Field label="State" required>
            <Input value={form.state} onChange={set('state')} />
          </Field>
          <Field label="ZIP" required>
            <Input value={form.postalCode} onChange={set('postalCode')} />
          </Field>
        </div>
        <Field label="Gate code" hint="optional">
          <Input value={form.gateCode} onChange={set('gateCode')} />
        </Field>

        <label className="flex items-center gap-2 text-[13px]">
          <input
            type="checkbox"
            checked={form.isPrimary}
            onChange={(e) => setForm((f) => ({ ...f, isPrimary: e.target.checked }))}
            className="size-4 accent-current text-accent"
          />
          <span>Primary address — the one a new job defaults to</span>
        </label>

        {error && (
          <p className="rounded-lg bg-danger-soft px-3 py-2 text-[13px] text-danger">{error}</p>
        )}
      </div>
    </Modal>
  );
}
